<template>
  <v-tabs
    v-model="tab"
    class="elevation-2"
    dark
  >
    <v-tabs-slider></v-tabs-slider>
    <v-tab href="#controller-tab">Controller</v-tab>
    <v-tab href="#misc-tab">Misc</v-tab>
    <v-tab href="#communication-tab">Communication</v-tab>
    <v-tab href="#debug-tab">Debug</v-tab>

    <v-tab-item eager value="controller-tab">
      <v-container class="rs-container" fluid fill-height>
        <div ref="container" class="rs-controller" tabindex="0" @keydown="onKeyDown" @keyup="onKeyUp" @mousedown="onMouseDown" @mouseup="onMouseUp" @touchstart="onTouchStart" @touchend="onTouchEnd" @touchcancel="onTouchCancel">
          <Led
            :bridge="bridge"
            width="330" height="330"
            left="590"
            top="100"
            :led="leds[0]"
          />
          <div class="rs-background"></div>
          <div class="rs-display">
            <Display ref="display" type="X19229" :bridge="bridge" :displayBuffer="displayBuffer" />
          </div>
          <v-container class="rs-controls">
            <v-row>
              <v-col cols="8">
                <v-row dense>
                  <v-col cols="3" v-for="(button, index) in buttons" :key="`button-${index}`">
                    <SensorButton
                      v-model="button.value"
                      :conversion="button.conversion"
                      :name="button.name"
                      :initialValue="button.initialValue"
                      @input="onSensorInput(button, button.platformIndex == null ? index : button.platformIndex, $event)"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="4">
                <v-row dense>
                  <v-col cols="4" v-for="(output, index) in outputs" :key="`output-${index}`">
                    <Output
                      :value="output.value"
                      :conversion="output.conversion"
                      :name="output.name"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-container>
    </v-tab-item>
    <v-tab-item eager value="misc-tab">
      <v-container>
        <v-row>
          <v-col>
            <LanguageSelect
              ref="languageSelect"
              title="Regler-Sprache"
              :bridge="bridge"
              :languages="languages"
              :localStoragePrefix="localStoragePrefix"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <LanguageSelect
              title="Screenshot-Sprache"
              ref="screenshotLanguageSelect"
              :languages="languages"
              :localStoragePrefix="localStoragePrefix"
              />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-switch
              v-model="drawBorderAroundScreenshot"
              label="Draw border around screenshot"
            ></v-switch>
            <v-btn
              @click="saveScreenshot()"
            >
              Save screenshot
            </v-btn>
            <v-btn
              @click="saveScreenshots()"
            >
              Save screenshots in all languages
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              @click="reset()"
            >
              Reset
            </v-btn>
            <v-btn
              @click="exportStorage()"
            >
              Export Storage
            </v-btn>
            <v-btn
              @click="$refs.storageFileInput.click()"
            >
              Import Storage &amp; Reset
            </v-btn>
            <v-btn
              @click="eraseStorage()"
            >
              Erase Storage &amp; Reset
            </v-btn>
            <input
              ref="storageFileInput"
              type="file"
              style="display: none"
              @change="onStorageFileInputChanged"
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <SDCard
              :bridge="bridge"
            />

          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <LogPanel :logLines="logLines" />
          </v-col>
          <v-col cols="6">
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
    <v-tab-item eager value="communication-tab">
      <CommPanel
        ref="communication"
        :uarts="uarts"
        :bridge="bridge"
        :vbusSpecifications="vbusSpecifications"
      />
    </v-tab-item>
    <v-tab-item eager value="debug-tab">
      <v-container fluid>
        <v-row>
          <v-col cols="6">
            <v-treeview
              :items="debugItems"
              dense
            ></v-treeview>
          </v-col>
          <v-col cols="6">
            <v-btn @click="bridge.start()">Start</v-btn>
            <v-btn @click="bridge.stop()">Stop</v-btn>
            <v-btn @click="generateSupportReport">Generate support report</v-btn>
            <v-textarea
              auto-grow
              dense
              readonly
              :value="supportReportText"
              class="rs-support-report"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
  </v-tabs>
</template>

<script>
import Display from '@/components/Display';
import SensorButton from '@/components/SensorButton';
import LanguageSelect from '@/components/LanguageSelect';
import SDCard from '@/components/SDCard';
import Output from '@/components/Output';
import LogPanel from '@/components/LogPanel';
import CommPanel from '@/components/communication/CommPanel';
import Led from '@/components/Led';
import Uart from '@/mixins/Uart';
import Bridgeable from '@/mixins/Bridgeable';
import Screenshotable from '@/mixins/Screenshotable';

import state from '@/state';
import { startDownload, readFile } from '@/utils';

export default {
  props: [
    'localStoragePrefix',
    'buttons',
    'outputs',
    'leds',
    'uarts',
    'languages',
    'bridge',
    'vbusSpecifications',
  ],

  data: () => ({
    logLines: [],
    buttonRefs: null,
    outputRefs: null,
    lastMouseButtonType: 0,
    activeTouches: [],
    tab: null,
    debugItems: [],
    supportReportText: '',
    drawBorderAroundScreenshot: false,
    displayBuffer: null,
  }),

  components: {
    Display,
    Led,
    SensorButton,
    Output,
    LanguageSelect,
    SDCard,
    LogPanel,
    CommPanel,
  },

  mixins: [
    Bridgeable,
    Screenshotable,
    Uart,
  ],

  mounted() {

  },

  destroyed() {
  },

  methods: {
    preparePlatform() {
      this.debugItems = this.bridge.debugProbes;

      for (let index = 0; index < this.buttons.length; index++) {
        this.onSensorInput(this.buttons [index], index, this.buttons [index].value);
      }

      this.$refs.container.focus();

      this.buttonRefs = this.buttons.reduce((memo, button) => {
        if (button.ref) {
          memo [button.ref] = button;
        }
        return memo;
      }, {});

      this.outputRefs = this.outputs.reduce((memo, output) => {
        if (output.ref) {
          memo [output.ref] = output;
        }
        return memo;
      }, {});

      this.initializeCommunication();

      this.$emit('initialized');
    },

    onKeyDown(ev) {
      this.onKeyEvent(ev, true);
    },

    onKeyUp(ev) {
      this.onKeyEvent(ev, false);
    },

    onKeyEvent(ev, state) {
      let buttonType;
      switch (ev.key) {
      case 'ArrowUp':
        buttonType = 3;
        break;
      case 'ArrowDown':
        buttonType = 4;
        break;
      case 'ArrowLeft':
        buttonType = 1;
        break;
      case 'ArrowRight':
        buttonType = 2;
        break;
      case 'Enter':
        buttonType = 5;
        break;
      case 'Backspace':
        buttonType = 6;
        break;
      default:
        buttonType = 0;
        break;
      }

      // console.log(buttonType, state);

      if (buttonType > 0) {
        ev.preventDefault();
        ev.stopPropagation();

        this.bridge.setButtonState(buttonType, state);
      }
    },

    getButtonTypeByCoord(x, y) {
      const coords = {
        x,
        y,
      };

      // console.log(coords);

      function checkCoords(x, y, w, h) {
        let result;
        if (coords.x < x) {
          result = false;
        } else if (coords.x >= (x + w)) {
          result = false;
        } else if (coords.y < y) {
          result = false;
        } else if (coords.y >= (y + h)) {
          result = false;
        } else {
          result = true;
        }
        return result;
      }

      let buttonType;
      if (checkCoords(614, 208, 100, 100)) {
          buttonType = 1; // Links
      } else if (checkCoords(710, 111, 100, 100)) {
          buttonType = 3; // Oben
      } else if (checkCoords(710, 208, 100, 100)) {
          buttonType = 5; // Mitte
      } else if (checkCoords(710, 306, 100, 100)) {
          buttonType = 4; // Unten
      } else if (checkCoords(816, 208, 100, 100)) {
          buttonType = 2; // Rechts
      } else if (checkCoords(605, 142, 100, 100)) {
          buttonType = 6; // Oben links
      } else if (checkCoords(605, 323, 100, 100)) {
          buttonType = 7; // Unten links
      }

      return buttonType;
    },

    onMouseDown(ev) {
      if (this.lastMouseButtonType) {
        this.onMouseEvent(ev, false);

        this.lastMouseButtonType = 0;
      }

      const buttonType = this.getButtonTypeByCoord(ev.offsetX, ev.offsetY);
      this.lastMouseButtonType = buttonType;

      this.onMouseEvent(ev, true);
    },

    onMouseUp(ev) {
      this.onMouseEvent(ev, false);

      this.lastMouseButtonType = 0;
    },

    onMouseEvent(ev, state) {
      const buttonType = this.lastMouseButtonType;

      if (buttonType > 0) {
        ev.preventDefault();
        ev.stopPropagation();

        this.bridge.setButtonState(buttonType, state);
      }
    },

    onTouchStart(ev) {
      this.onTouchEvent(ev, true);
    },

    onTouchEnd(ev) {
      this.onTouchEvent(ev, false);
    },

    onTouchCancel(ev) {
      this.onTouchEvent(ev, false);
    },

    onTouchEvent(ev) {
      let stopEvent = false;

      const activeTouches = [];
      for (let index = 0; index < ev.touches.length; index++) {
        const touch = ev.touches.item(index);

        let touchInfo = this.activeTouches.find(info => info.identifier === touch.identifier);
        if (!touchInfo) {
          const rect = touch.target.getBoundingClientRect();

          const x = touch.clientX - rect.x;
          const y = touch.clientY - rect.y;


          const buttonType = this.getButtonTypeByCoord(x, y);

          if (buttonType) {
            this.bridge.setButtonState(buttonType, true);
            stopEvent = true;
          }

          touchInfo = {
            identifier: touch.identifier,
            buttonType,
            x,
            y,
          };
        }

        activeTouches.push(touchInfo);
      }

      for (const touchInfo of this.activeTouches) {
        if (!activeTouches.includes(touchInfo)) {
          if (touchInfo.buttonType) {
            this.bridge.setButtonState(touchInfo.buttonType, false);
            stopEvent = true;
          }
        }
      }

      if (stopEvent) {
        ev.preventDefault();
        ev.stopPropagation();
      }

      this.activeTouches = activeTouches;
    },

    handleInterval() {
      if (this.bridge.isDisplayInvalidated()) {
        this.displayBuffer = this.bridge.getDisplayBuffer().slice();
      }

      if (this.bridge.isLedInvalidated()) {
          const ColorShiftOff = 0;
          const ColorShiftRed = 8;
          const ColorShiftGreen = 12;
          const ColorShiftOrange = 16;

          for (let index = 0; index < 1; index++) {
            if (!this.leds [index]) {
              continue;
            }
            const state = this.bridge.getLedStaticState(index);

            this.leds [index].state = state;
            this.leds [index].TimerColor0 = ((state >> ColorShiftOff) & 15) * 25;
            this.leds [index].TimerColor1 = ((state >> ColorShiftRed) & 15) * 25;
            this.leds [index].TimerColor2 = ((state >> ColorShiftGreen) & 15) * 25;
            this.leds [index].TimerColor3 = ((state >> ColorShiftOrange) & 15) * 25;
          }
      }

      if (this.bridge.isOutputInvalidated()) {
        for (let index = 0; index < 19; index++) {
          if (!this.outputs [index]) {
            continue;
          }
          const value = this.bridge.getOutputValueAndType(index);
          this.outputs [index].value = value;
        }
      }
    },

    onSensorInput(button, index, value) {
      // Error.stackTraceLimit = Infinity;
      // console.log(index, button.name, button.value, value, new Error().stack);
      if (!this.bridge || !value) {
        // nop
      } else {
        this.bridge.setInputValueForType(index, value.type, value.value);
      }
    },

    async generateSupportReport() {
      const jsonString = JSON.stringify(this.debugItems);
      const jsonBuffer = Buffer.from(jsonString);
      const b64String = jsonBuffer.toString('base64');
      const lines = b64String.replace(/(.{76})/g, '$1\n').trim();
      const report = `----BEGIN WASI CONTROLLER TRAINER SUPPORT REPORT----\n${lines}\n----END WASI CONTROLLER TRAINER SUPPORT REPORT----\n`;
      this.supportReportText = report;

      await navigator.clipboard.writeText(report);
    },

    exportStorage() {
      startDownload(`storage-${Date.now()}.cbor`, 'application/octet-stream', this.storage);
    },

    onStorageFileInputChanged() {
      state.runAsync(async () => {
        const buffer = await readFile(this.$refs.storageFileInput);

        this.storage = buffer;
        this.reset();
      });
    },

    eraseStorage() {
      this.storage = null;

      const storageLsKey = `${this.localStoragePrefix}Storage`;
      localStorage.removeItem(storageLsKey);

      this.reset();
    },
  },
};
</script>

<style scoped>
.rs-container {
  padding: 0;
}

.rs-controller {
  width: 1024px;
  height: 704px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
}

.rs-background {
  width: 1024px;
  height: 704px;
  position: absolute;
  background: url('/DeviceBx_Resol.png');
  background-size: 1024px 704px;
}

.rs-display {
  position: absolute;
  width: 410px;
  height: 140px;
  left: 120px;
  top: 145px;
}

.rs-controls {
  position: absolute;
  left: 0px;
  top: 500px;
  width: 1024px;
  height: 400px;
  max-width: 1024px;
}

.rs-support-report {
  font-family: 'Courier New', Courier, monospace;
  font-size: 10px;
}

.rs-support-report >>> textarea {
  line-height: 12px;
}
</style>
